/* eslint-disable max-lines */
import { useForm, useFormState } from 'react-final-form';
import { useDispatch } from 'react-redux';
import React, { useEffect, useRef, useState } from 'react';
import { debounce, isEmpty, map, slice } from 'lodash';
import Tooltip from '@mui/material/Tooltip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { LoadingButton } from '@mui/lab';

import TextField from 'common/forms/TextField';
import DateField from 'common/forms/DateField';
import { CATALOG_DISABLED_SELECTION_NOTE } from 'common/constants/catalogStatus';
import AutoSuggest from 'common/forms/AutoSuggest';
import SigsAutoSuggest from 'common/forms/SigsAutoSuggest';
import { listCatalogProductsByDrugName, listDrugProfilesByDrugName } from 'store/thunks/drugThunks';
import { EnableInventoryLookup } from 'common/utils';
import { validateMinNumber, validateNumber } from 'common/forms/formValidations';
import { SigLengthLimits } from 'enums/SigManagement.enum';

import { ItemLabelValue } from '../../PatientView/components/PrescriptionsTab/PatientPrescriptionCustomLabels';

const ModifyPrescriptionForm = ({
  expanded,
  handleChange,
  handleRemoveItem,
  prescriptionIndex,
}) => {
  const formState = useFormState();
  const dispatch = useDispatch();
  const [drugs, setDrugs] = useState([]);
  const [drugLoading, setDrugLoading] = useState(false);
  const [selectedDrugProfile, setSelectedDrugProfile] = useState();

  const fetchDrugs = debounce((searchText) => {
    setDrugLoading(true);
    // POM-5805: gated on INVENTORY_LOOKUP — ON searches the catalog (Product ID), OFF keeps
    // the legacy drug-profile search until the switch is permanently on.
    const searchThunk = EnableInventoryLookup
      ? listCatalogProductsByDrugName(searchText)
      : listDrugProfilesByDrugName(searchText);
    dispatch(searchThunk)
      .then((results) => {
        setDrugs(results || []);
      })
      .finally(() => setDrugLoading(false));
  }, 500);

  return (
    <Accordion
      expanded={expanded === prescriptionIndex}
      onChange={handleChange(prescriptionIndex)}
      id={`Prescription-${prescriptionIndex}-header`}
      slotProps={{
        transition: {
          timeout: {
            exit: 0,
          },
        },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography
          component='span'
          sx={{ width: '20%', flexShrink: 0, display: 'flex', alignItems: 'center' }}
          color='primary'
        >
          Item #{prescriptionIndex + 1}
          {!isEmpty(formState.errors?.prescriptionInput?.[prescriptionIndex]) && (
            <Tooltip title='Information Needed'>
              <ErrorOutlineIcon color='error' sx={{ ml: 2 }} />
            </Tooltip>
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ position: 'relative' }}>
        <Button
          variant='outlined'
          color='error'
          sx={{ position: 'absolute', top: 8, right: 16 }}
          disabled={formState.values.prescriptionInput.length < 2}
          onClick={() => handleRemoveItem(prescriptionIndex)}
        >
          Remove
        </Button>
        <Grid container sx={{ px: 2 }}>
          <Grid container item xs={12} columnSpacing={3}>
            <Grid item xs={12}>
              <Typography variant='h6' sx={{ fontWeight: 600, mb: 3 }}>
                Prescription Details
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <AutoSuggest
                id='ResolveMultiRxReviewDialog-Existing-Drug-Input'
                name={`prescriptionInput[${prescriptionIndex}].drug`}
                label={
                  EnableInventoryLookup ? 'Search Catalog Product' : 'Search Existing Drug Profile'
                }
                loading={drugLoading}
                options={[
                  ...map(drugs, (drugDetail) =>
                    EnableInventoryLookup
                      ? {
                          ...drugDetail,
                          label: drugDetail.displayName,
                          id: drugDetail.productId,
                          // POM-6306: informational only. A catalog product disabled for ordering
                          // is still the right choice for an order placed before it was withdrawn.
                          isCatalogDisabled: drugDetail.isCatalogEnabled === false,
                        }
                      : {
                          ...drugDetail,
                          label: drugDetail.drugName,
                          id: drugDetail.drugProfileId,
                        }
                  ),
                ]}
                fetchOptions={fetchDrugs}
                selected={selectedDrugProfile}
                handleSelect={setSelectedDrugProfile}
                isPrescriptionReview
              />

              {/* POM-6306: per item, not per dialog — this form resolves several prescriptions at
                  once, and a note covering all of them would not say which one it is about. */}
              {formState.values.prescriptionInput?.[prescriptionIndex]?.drug?.isCatalogDisabled && (
                <Alert severity='info' sx={{ mt: 2 }}>
                  {CATALOG_DISABLED_SELECTION_NOTE}
                </Alert>
              )}
            </Grid>

            <Grid item xs={12}>
              <SigsAutoSuggest
                id='ResolveMultiRxReviewDialog-Existing-Sig-Input'
                name={`prescriptionInput[${prescriptionIndex}].sig`}
                label='Search Existing Sig'
                maxLength={SigLengthLimits.SIG_MAX}
                isPrescriptionReview
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                type='refills'
                label='Refills'
                name={`prescriptionInput[${prescriptionIndex}].refills`}
                id='ResolveMultiRxReviewDialog-refills-input'
                validations={[
                  validateNumber('Provide a valid number', 0),
                  validateMinNumber('Refills should not be less than 0', 0),
                ]}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label='Quantity'
                name={`prescriptionInput[${prescriptionIndex}].quantity`}
                id='ResolveMultiRxReviewDialog-quantity-input'
                validations={[
                  validateNumber('Provide a valid number', 1),
                  validateMinNumber('Quantity should not be less than 1', 1),
                ]}
              />
            </Grid>

            <Grid item xs={6}>
              <DateField
                label='Expiration Date'
                name={`prescriptionInput[${prescriptionIndex}].expirationDate`}
                id='ResolveMultiRxReviewDialog-expirationDate-input'
                minDate={new Date(new Date().setDate(new Date().getDate() + 3))}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                multiline
                minRows={5}
                label='Notes'
                name={`prescriptionInput[${prescriptionIndex}].notes`}
                id='ResolveMultiRxReviewDialog-notes-input'
              />
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

ModifyPrescriptionForm.propTypes = {
  expanded: PropTypes.number.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleRemoveItem: PropTypes.func.isRequired,
  prescriptionIndex: PropTypes.number.isRequired,
};

const ResolveMultiRxReviewDialog = ({
  rxNumber,
  onClose,
  loading,
  invalid,
  showModal,
  handleSubmit,
  prescriptionData,
  onCloseTrigger,
}) => {
  const form = useForm();
  const formState = useFormState();
  const dialogContentRef = useRef(null);
  const [expanded, setExpanded] = useState(0);
  const [viewConfirmModal, setViewConfirmModal] = useState(false);
  const isMaxSplitLimitReached =
    formState.values.prescriptionInput.length ===
    Number(process.env.REACT_APP_DEFAULT_SPIT_CAPACITY || 5);

  const handleChange = (panel) => (_, isExpanded) => setExpanded(isExpanded ? panel : false);
  const handleOpenConfirmModal = () => setViewConfirmModal(true);
  const handleCloseConfirmModal = () => setViewConfirmModal(false);

  const handleAddPrescription = () => {
    const newState = [...formState.values.prescriptionInput, {}];
    form.change('prescriptionInput', newState);
    setExpanded(newState.length - 1);
    dialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRemovePrescription = (prescriptionIndex) => {
    const updatedPrescription = [
      ...slice(formState.values.prescriptionInput, 0, prescriptionIndex),
      ...slice(formState.values.prescriptionInput, prescriptionIndex + 1),
    ];
    form.change('prescriptionInput', updatedPrescription);
  };

  useEffect(() => {
    if (onCloseTrigger) {
      handleCloseConfirmModal();
    }
  }, [onCloseTrigger]);

  return (
    <>
      <Dialog maxWidth='sm' fullWidth open={viewConfirmModal}>
        <form noValidate onSubmit={handleSubmit}>
          <DialogTitle variant='h5' sx={{ my: 1 }}>
            Confirm Modify Item
          </DialogTitle>

          <DialogContent>
            {`You have split this prescription into `}
            <strong>{formState.values.prescriptionInput.length} </strong>
            separate items. Please review and confirm the changes. The original item
            <strong> {rxNumber}</strong>, will no longer be available for processing after this
            action.
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button variant='outlined' color='secondary' onClick={handleCloseConfirmModal}>
              Cancel
            </Button>
            <LoadingButton loading={loading} variant='contained' type='submit'>
              Confirm
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog maxWidth='lg' fullWidth open={showModal}>
        <form noValidate onSubmit={handleSubmit}>
          <DialogTitle variant='h5' sx={{ my: 2 }}>
            Modify Item
          </DialogTitle>

          <DialogContent sx={{ minHeight: 450 }} ref={dialogContentRef}>
            <Grid container spacing={4}>
              <Grid item xs={3.5}>
                <Typography variant='h6' gutterBottom sx={{ mb: 2 }}>
                  Original Prescription Details
                </Typography>
                <Grid container direction='column' spacing={1} sx={{ mt: 2 }}>
                  <Grid item>
                    <ItemLabelValue label='Drug' value={prescriptionData?.drug ?? 'N/A'} />
                  </Grid>
                  <Grid item>
                    <ItemLabelValue label='Strength' value={prescriptionData?.strength ?? 'N/A'} />
                  </Grid>
                  <Grid item>
                    <ItemLabelValue label='Quantity' value={prescriptionData?.quantityPrescribed} />
                  </Grid>
                  <Grid item>
                    <ItemLabelValue
                      label='Refills'
                      value={prescriptionData?.refillsRemaining || 'N/A'}
                    />
                  </Grid>
                  <Grid item>
                    <ItemLabelValue label='Form' value={prescriptionData?.form ?? 'N/A'} />
                  </Grid>
                  <Grid item>
                    <ItemLabelValue label='Notes' value={prescriptionData?.rxNotes || 'N/A'} />
                  </Grid>

                  <Grid item>
                    <ItemLabelValue
                      label='Direction'
                      value={prescriptionData.instructions || 'N/A'}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={0.1} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Divider orientation='vertical' flexItem />
              </Grid>

              <Grid item xs={8}>
                <Typography variant='h6' gutterBottom sx={{ mb: 2 }}>
                  Modify Prescription Items
                </Typography>
                {map(formState.values.prescriptionInput, (_, index) => (
                  <ModifyPrescriptionForm
                    key={index}
                    expanded={expanded}
                    prescriptionIndex={index}
                    handleChange={handleChange}
                    handleRemoveItem={handleRemovePrescription}
                  />
                ))}
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'row-reverse' }}>
                  <Tooltip
                    arrow
                    placement='top'
                    title={`${isMaxSplitLimitReached ? 'Maximum split limit reached' : ''}`}
                  >
                    <span>
                      <Button
                        variant='contained'
                        onClick={() => handleAddPrescription(formState.values.prescriptionInput)}
                        disabled={isMaxSplitLimitReached}
                      >
                        Add Item
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button variant='outlined' color='secondary' onClick={onClose}>
              Cancel
            </Button>
            <Button variant='contained' disabled={invalid} onClick={handleOpenConfirmModal}>
              Modify Items
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

ResolveMultiRxReviewDialog.propTypes = {
  invalid: PropTypes.bool,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  showModal: PropTypes.bool.isRequired,
  rxNumber: PropTypes.string.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  prescriptionData: PropTypes.shape({
    form: PropTypes.string.isRequired,
    drug: PropTypes.string.isRequired,
    rxNotes: PropTypes.string.isRequired,
    strength: PropTypes.string.isRequired,
    instructions: PropTypes.string.isRequired,
    quantityPrescribed: PropTypes.string.isRequired,
    refillsRemaining: PropTypes.string.isRequired,
  }).isRequired,
  onCloseTrigger: PropTypes.bool,
};

ResolveMultiRxReviewDialog.defaultProps = {
  invalid: false,
  loading: false,
  onCloseTrigger: null,
};

export default ResolveMultiRxReviewDialog;
